import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useUsers } from '@/hooks/useUsers'
import { useUpdateUserRole, type UserRole } from '@/hooks/useUpdateUserRole'
import { useRemoveUser } from '@/hooks/useRemoveUser'
import { UserTable } from '@/components/users/UserTable'
import { Button } from '@/components/ui/button'

export default function UserManagement() {
  const navigate = useNavigate()

  // Get user from localStorage
  const userStr = localStorage.getItem('user')
  const currentUser = userStr ? JSON.parse(userStr) : null

  // Local state for inline editing (passed to UserTable)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [editingRole, setEditingRole] = useState<string | null>(null)
  const [newRole, setNewRole] = useState<string>('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)

  // Use API hooks
  const { users, loading, error, refetchUsers } = useUsers(currentUser?.companyId)
  const { updateUserRole } = useUpdateUserRole()
  const { removeUser } = useRemoveUser()

  useEffect(() => {
    if (!currentUser) {
      navigate('/login')
      return
    }

    // Check if user is company admin or super admin
    if (currentUser.role !== 'company_admin' && currentUser.role !== 'super_admin') {
      navigate('/dashboard')
      return
    }
  }, [navigate, currentUser])

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-muted-foreground">Loading users...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page info */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p className="text-muted-foreground">
              {currentUser?.role === 'super_admin'
                ? 'Manage users across the platform'
                : 'Manage users and their roles in your company'}
            </p>
          </div>

          {/* Invite User Button */}
          <Button
            onClick={() => navigate('/invite')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-destructive/10 border border-destructive/50 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-destructive" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
              <button
                onClick={() => refetchUsers()}
                className="ml-auto text-sm text-destructive hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* User Table */}
        <UserTable
          users={users}
          loading={loading}
          currentUser={currentUser}
          actionLoadingUserId={actionLoading}
          editingRoleUserId={editingRole}
          newRole={newRole}
          showDeleteConfirmUserId={showDeleteConfirm}
          onEditRole={(userId, currentRole) => {
            setEditingRole(userId)
            setNewRole(currentRole)
          }}
          onNewRoleChange={setNewRole}
          onRoleChange={async (userId, newRole) => {
            try {
              setActionLoading(userId)
              await updateUserRole(userId, newRole as UserRole)
              setEditingRole(null)
              refetchUsers()
            } catch (err) {
              console.error('Failed to update user role:', err)
            } finally {
              setActionLoading(null)
            }
          }}
          onCancelEdit={() => setEditingRole(null)}
          onShowDeleteConfirm={(userId) => setShowDeleteConfirm(userId)}
          onCancelDelete={() => setShowDeleteConfirm(null)}
          onDeleteUser={async (userId) => {
            try {
              setActionLoading(userId)
              await removeUser(userId)
              setShowDeleteConfirm(null)
              refetchUsers()
            } catch (err) {
              console.error('Failed to remove user:', err)
            } finally {
              setActionLoading(null)
            }
          }}
        />

        {/* Back to Dashboard Button */}
        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  )
}
